import React from "react";
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Toolbar,
  useMediaQuery,
  Badge,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import CartIcon from "@mui/icons-material/ShoppingCart";

import logo from "../../assets/logo.svg";
import logo2 from "../../assets/logo-mid.svg";
import styles from "./Header.module.scss";
import arrow from "../../assets/Arrow_B.svg";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import { readCartItems, subscribeToCartChanges } from "../../utils/cartStorage";

export default function Header() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const nav = t("header.nav", { returnObjects: true });

  const [drawerOpen, setDrawerOpen] = React.useState(false);

  // Desktop dropdown
  const [openMenu, setOpenMenu] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const closeTimeoutRef = React.useRef(null);

  // Mobile collapses
  const [mobileOpen, setMobileOpen] = React.useState({});

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const openDesktopMenu = (label, element) => {
    clearCloseTimeout();
    setOpenMenu(label);
    setAnchorEl(element);
  };

  const closeDropdown = () => {
    clearCloseTimeout();
    setOpenMenu(null);
    setAnchorEl(null);
  };

  const scheduleCloseDropdown = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpenMenu(null);
      setAnchorEl(null);
    }, 120);
  };

  React.useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, []);

  const toggleMobileGroup = (label) => {
    setMobileOpen((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const currentDropdown =
    openMenu &&
    nav.find((item) => item.type === "dropdown" && item.label === openMenu);

  const [cartCount, setCartCount] = React.useState(0);

  const updateCartCount = React.useCallback(() => {
    // readCartItems handles the legacy-key migration and always returns the
    // canonical array under the versioned key. Using it here means the badge
    // also stays accurate for users who never had legacy data.
    const bookingsCount = readCartItems("bookings").length;
    const checkupsCount = readCartItems("checkups").length;
    const gymPassesCount = readCartItems("passes").length;

    setCartCount(bookingsCount + checkupsCount + gymPassesCount);
  }, []);

  React.useEffect(() => {
    updateCartCount();
    // subscribeToCartChanges covers "cartUpdated", cross-tab "storage", and
    // "pageshow" with event.persisted (bfcache restore on browser Back).
    return subscribeToCartChanges(updateCartCount);
  }, [updateCartCount]);

  return (
    <>
      <AppBar position="sticky" elevation={0} className={styles.appBar}>
        <div className={styles.inner}>
          <Toolbar className={styles.toolbar}>
            <RouterLink
              to="/"
              className={styles.logoWrap}
              aria-label={t("common.homeAria")}
            >
              <Box
                component="img"
                src={logo}
                alt="AlfaGym"
                className={styles.logo}
              />
            </RouterLink>

            <Box sx={{ flex: 1 }} />

            {isMobile && (
              <IconButton
                onClick={() => setDrawerOpen(true)}
                className={styles.burger}
                aria-label={t("common.openMenu")}
              >
                <MenuIcon />
              </IconButton>
            )}

            {!isMobile && (
              <Box className={styles.desktopNav}>
                <Box className={styles.navLinks}>
                  {nav.map((item) => {
                    if (item.type === "dropdown") {
                      const isActive = openMenu === item.label;

                      return (
                        <Box
                          key={item.label}
                          className={styles.navItemWrap}
                          onMouseEnter={(e) =>
                            openDesktopMenu(item.label, e.currentTarget)
                          }
                          onMouseLeave={scheduleCloseDropdown}
                        >
                          <Button
                            disableRipple
                            className={styles.navBtn}
                            component={RouterLink}
                            to={item.to}
                          >
                            {item.label}
                          </Button>

                          <Box
                            className={`${styles.dropdownTrigger} ${isActive ? styles.dropdownTriggerActive : ""
                              }`}
                            aria-hidden="true"
                          >
                            <ExpandMoreIcon className={styles.chev} />
                          </Box>
                        </Box>
                      );
                    }

                    return (
                      <Button
                        key={item.label}
                        disableRipple
                        className={styles.navBtn}
                        component={RouterLink}
                        to={item.to}
                      >
                        {item.label}
                      </Button>
                    );
                  })}
                </Box>

                <Box className={styles.navSide}>
                  <Button
                    disableRipple
                    className={styles.ctaBtn}
                    component={RouterLink}
                    to="/rezerwacja#kalendarz"
                  >
                    {t("common.book")}
                    <img
                      className={styles.arrowIcon}
                      src={arrow}
                      alt=""
                      aria-hidden="true"
                    />
                  </Button>

                  <LanguageSwitcher />

                  <Box className={styles.socialRow}>
                    <IconButton href="https://www.instagram.com/alfagym_studio/"
                      target="_blank"
                      rel="noopener noreferrer" className={styles.socialIcon} aria-label={t("common.instagram")}>
                      <InstagramIcon />
                    </IconButton>
                    {/* <IconButton className={styles.socialIcon} aria-label={t("common.facebook")}>
                      <FacebookIcon />
                    </IconButton> */}
                    <IconButton
                      className={styles.socialIcon}
                      component={RouterLink}
                      to="/checkout"
                      aria-label={t("common.cartAria", { count: cartCount })}
                    >
                      <Badge badgeContent={cartCount} className={styles.cartBadge} showZero={false}>
                        <CartIcon />
                      </Badge>
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )}
          </Toolbar>
        </div>
      </AppBar>

      {!isMobile && currentDropdown && (
        <Popper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom-start"
          className={styles.popper}
        >
          <Paper
            className={styles.menuPaper}
            elevation={0}
            onMouseEnter={clearCloseTimeout}
            onMouseLeave={scheduleCloseDropdown}
          >
            <Box className={styles.menuList}>
              {currentDropdown.items.map((subItem) => (
                <Button
                  key={subItem.label}
                  component={RouterLink}
                  to={subItem.to}
                  onClick={closeDropdown}
                  className={styles.menuItem}
                >
                  {subItem.label}
                </Button>
              ))}
            </Box>
          </Paper>
        </Popper>
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ className: styles.drawerPaper }}
      >
        <RouterLink to="/" onClick={() => setDrawerOpen(false)}>
          <Box className={styles.drawerTop}>
            <Box
              component="img"
              src={logo2}
              alt="AlfaGym"
              className={styles.drawerLogo}
            />
          </Box>
        </RouterLink>

        <List className={styles.drawerList}>
          {nav.map((item) => {
            if (item.type === "dropdown") {
              const isOpen = Boolean(mobileOpen[item.label]);

              return (
                <Box key={item.label}>
                  <Box className={styles.drawerRow}>
                    <ListItemButton
                      className={`${styles.drawerItem} ${styles.drawerMainLink}`}
                      component={RouterLink}
                      to={item.to}
                      onClick={() => setDrawerOpen(false)}
                    >
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ className: styles.drawerText }}
                      />
                    </ListItemButton>

                    <IconButton
                      className={`${styles.drawerToggle} ${isOpen ? styles.drawerChevOpen : ""
                        }`}
                      aria-label={t("common.expandMenu", { label: item.label })}
                      onClick={() => toggleMobileGroup(item.label)}
                    >
                      <ExpandMoreIcon className={styles.drawerChev} />
                    </IconButton>
                  </Box>

                  <Collapse in={isOpen} timeout={180} unmountOnExit>
                    <List component="div" disablePadding>
                      {item.items.map((subItem) => (
                        <ListItemButton
                          key={subItem.label}
                          onClick={() => setDrawerOpen(false)}
                          className={`${styles.drawerItem} ${styles.drawerSubItem}`}
                          component={RouterLink}
                          to={subItem.to}
                        >
                          <ListItemText
                            primary={subItem.label}
                            primaryTypographyProps={{
                              className: styles.drawerSubText,
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            }

            return (
              <ListItemButton
                key={item.label}
                onClick={() => setDrawerOpen(false)}
                className={`${styles.drawerItem} ${styles.drawerActionItem}`}
                component={RouterLink}
                to={item.to}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ className: styles.drawerText }}
                />
              </ListItemButton>
            );
          })}

          <Box className={styles.drawerCtaWrap}>
            <Button
              fullWidth
              disableRipple
              className={styles.drawerCtaBtn}
              component={RouterLink}
              to="/rezerwacja#kalendarz"
              onClick={() => setDrawerOpen(false)}
            >
              {t("common.book")}
              <img
                className={styles.arrowIcon}
                src={arrow}
                alt=""
                aria-hidden="true"
              />
            </Button>

            <Box sx={{ mt: 1.5 }}>
              <LanguageSwitcher />
            </Box>

            <Box className={styles.drawerSocial}>
              <IconButton className={styles.socialIcon} aria-label={t("common.instagram")}>
                <InstagramIcon />
              </IconButton>
              <IconButton className={styles.socialIcon} aria-label={t("common.facebook")}>
                <FacebookIcon />
              </IconButton>
              <IconButton
                className={styles.socialIcon}
                component={RouterLink}
                to="/checkout"
                aria-label={t("common.cartAria", { count: cartCount })}
              >
                <Badge badgeContent={cartCount} className={styles.cartBadge} color="error" showZero={false}>
                  <CartIcon />
                </Badge>
              </IconButton>
            </Box>
          </Box>
        </List>
      </Drawer>
    </>
  );
}
